import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './index.module.sass';
import classnames from 'classnames/bind';
import LoginFrame from 'layout/login-frame';
import TextInput from 'componets/text-input';
import Button from 'componets/button';
import {
  CHANGE_CONFIRMED_PASSWORD,
  CHANGE_NEW_PASSWORD,
  CHANGE_EMAIL,
  CHANGE_COMPANY_NAME,
  useLoginContext,
} from 'features/login/use-login';
import UpdatePassword from 'modules/update-password';
import analytics from 'service/analytics';
const cx = classnames.bind(styles);

const OnboardingForm = () => {
  const { state, dispatch, onUpdatePassword, onUpdateAccount, onLockTab, onUnlockTab } = useLoginContext();

  const isPasswordSetting = state.onboardingStep === 'password';
  const [frameHeight, setFrameHeight] = useState(0);
  const passwordSettingRef = useRef();
  const accountSettingRef = useRef();

  useEffect(() => {
    if (isPasswordSetting) {
      setFrameHeight(passwordSettingRef.current.scrollHeight);
    } else {
      setFrameHeight(accountSettingRef.current.scrollHeight);
    }
  }, [
    isPasswordSetting,
    state.newPasswordErrorMessage,
    state.confirmedPasswordErrorMessage,
    state.emailErrorMessage,
    state.companyNameErrorMessage,
    state.invoiceErrorMessage,
  ]);

  const onChangeNewPassword = useCallback(
    value => {
      dispatch({
        type: CHANGE_NEW_PASSWORD,
        payload: value,
      });
    },
    [dispatch]
  );

  const onChangeConfirmedPassword = useCallback(
    value => {
      dispatch({
        type: CHANGE_CONFIRMED_PASSWORD,
        payload: value,
      });
    },
    [dispatch]
  );

  const onChangeEmail = useCallback(
    value =>
      dispatch({
        type: CHANGE_EMAIL,
        payload: value,
      }),
    [dispatch]
  );

  const onChangeCompanyName = useCallback(
    value =>
      dispatch({
        type: CHANGE_COMPANY_NAME,
        payload: value,
      }),
    [dispatch]
  );

  const onNewPasswordFocus = () => {
    analytics.track('登入頁_首次登入_新密碼_click');
    onUnlockTab();
  };

  const onConfirmedPasswordFocus = () => {
    analytics.track('登入頁_首次登入_確認新密碼_click');
    onLockTab();
  };

  const onUpdatePasswordClick = () => {
    analytics.track('登入頁_首次登入_密碼頁下一步_click');
    onUpdatePassword(true);
  };

  const onEmailFocus = () => {
    analytics.track('登入頁_首次登入_電子信箱_click');
    onUnlockTab();
  };

  const onCompanyNameFocus = () => {
    analytics.track('登入頁_首次登入_帳號名稱_click');
    onUnlockTab();
  };

  const onUpdateAccountClick = () => {
    analytics.track('登入頁_首次登入_帳號資訊送出_click');
    onUpdateAccount();
  };

  const OnboardingPasswordStep = () => {
    const passwordStepClass = cx({
      'password-setting': true,
      out: !isPasswordSetting,
    });

    return (
      <div className={passwordStepClass} ref={passwordSettingRef}>
        <div className={styles.title}>設定您的登入密碼</div>
        <UpdatePassword
          newPassword={state.newPassword}
          newPasswordErrorMessage={state.newPasswordErrorMessage}
          onChangeNewPassword={onChangeNewPassword}
          confirmedPassword={state.confirmedPassword}
          confirmedPasswordErrorMessage={state.confirmedPasswordErrorMessage}
          onChangeConfirmedPassword={onChangeConfirmedPassword}
          onUpdatePassword={onUpdatePasswordClick}
          onNewPasswordFocus={onNewPasswordFocus}
          onConfirmedPasswordFocus={onConfirmedPasswordFocus}
        />
        <Button
          theme="black"
          size="full-width"
          className={styles['button']}
          onClick={onUpdatePasswordClick}
          loading={state.loading}
        >
          下一步
        </Button>
      </div>
    );
  };

  const OnboardingAccountStep = () => {
    const accountInfoStepClass = cx({
      'account-setting': true,
      in: !isPasswordSetting,
    });
    return (
      <div className={accountInfoStepClass} ref={accountSettingRef}>
        <div className={styles.title}>設定您的帳號資訊</div>
        <div className={styles.form}>
          <div className={styles['form-input']}>
            <label className={styles['form-label']}>電子信箱</label>
            <TextInput
              type="email"
              inputmode="email"
              label="電子信箱"
              placeholder="輸入 Email 地址"
              value={state.email}
              errorMessage={state.emailErrorMessage}
              onTextChange={onChangeEmail}
              onFocus={onEmailFocus}
            />
            <span className={styles.info}>消費明細將會寄送至此電子信箱。</span>
          </div>
          <div className={styles['form-input']}>
            <label className={styles['form-label']}>帳號名稱</label>
            <TextInput
              label="帳號名稱"
              placeholder="輸入企業名稱或使用者姓名"
              value={state.companyName}
              errorMessage={state.companyNameErrorMessage}
              onTextChange={onChangeCompanyName}
              onFocus={onCompanyNameFocus}
            />
          </div>
        </div>
        <Button
          theme="black"
          size="full-width"
          loading={state.loading}
          className={styles['button']}
          onClick={onUpdateAccountClick}
        >
          送出
        </Button>
      </div>
    );
  };

  const Progress = () => {
    const progressClass = cx({
      progress: true,
      [state.onboardingStep]: true,
    });

    const step = isPasswordSetting ? 1 : 2;

    return (
      <div className={progressClass}>
        <span>完成設定：步驟&nbsp;{step}/2</span>
        <div className={styles['bar']} />
      </div>
    );
  };

  return (
    <>
      <LoginFrame>
        <Progress />
        <div className={styles.container} style={{ height: frameHeight }}>
          <div className={styles['group']}>
            {OnboardingPasswordStep()}
            {OnboardingAccountStep()}
          </div>
        </div>
      </LoginFrame>
    </>
  );
};

export default OnboardingForm;
